import type { Milestone, ScheduleMilestone, SpecSchedule } from '../types';
import { formatSpecRowTitle } from './specDisplay';
import { milestoneStatusLabel, milestoneStatusOptions } from './ipcStatusEnums';

export const SPEC_TERMINATE_NODE_STATUS_VALUES = [
  'completed',
  'terminated',
  'in_progress',
  'pending',
] as const;

export type SpecTerminateNodeChoice = (typeof SPEC_TERMINATE_NODE_STATUS_VALUES)[number];

export function getSpecTerminateNodeStatusOptions(): { value: SpecTerminateNodeChoice; label: string }[] {
  const fromBase = milestoneStatusOptions();
  const byValue = new Map(fromBase.map(o => [o.value, o.label]));
  return SPEC_TERMINATE_NODE_STATUS_VALUES.map(value => ({
    value,
    label: byValue.get(value) || milestoneStatusLabel(value),
  }));
}

export type TerminateMatrixCol = {
  colKey: string;
  specId: string;
  /** 0 表示整列对应规格（无关联 SKU） */
  ipdSkuId: number;
  title: string;
  skuCode?: string;
  specTerminal: boolean;
  /** 该列所属规格的 SKU 数，跨规格合并节点只在规格的第一列计入工时 */
  specSkuCount: number;
  isFirstColOfSpec: boolean;
};

export type TerminateMatrixCell = {
  statusBefore: string;
  baselineManDays: number;
  autoCompletion?: boolean;
};

export function terminateCellKey(colKey: string, milestoneId: string | number) {
  return `${colKey}:${milestoneId}`;
}

export function defaultTerminateChoice(statusBefore: string): SpecTerminateNodeChoice {
  const s = (statusBefore || '').toLowerCase();
  if (s === 'in_progress') return 'terminated';
  if (s === 'terminated') return 'terminated';
  if (s === 'completed') return 'completed';
  return 'pending';
}

export function isTerminateCellLocked(statusBefore: string, specTerminal: boolean): boolean {
  if (specTerminal) return true;
  return (statusBefore || '').toLowerCase() === 'completed';
}

export function skuSpecColumnTitle(sku: {
  specText?: string;
  skuCode?: string;
  name?: string;
}): string {
  return (sku.specText || sku.skuCode || sku.name || '—').trim() || '—';
}

export function effectiveSkuNodeStatus(
  spec: SpecSchedule,
  milestoneId: string,
  ipdSkuId?: number,
): string {
  if (ipdSkuId && ipdSkuId > 0) {
    const overrides = (spec.skuMilestones ?? [])
      .filter(m => m.ipdSkuId === ipdSkuId && String(m.milestoneId) === String(milestoneId))
      .sort((a, b) => b.id - a.id);
    const status = overrides[0]?.status;
    if (status) return status.toLowerCase();
  }
  const cell: ScheduleMilestone | undefined = spec.milestones?.find(
    m => String(m.milestoneId) === String(milestoneId),
  );
  return (cell?.status || 'pending').toLowerCase();
}

/** 多 SKU 规格拆成多列，列头为 SKU 规格名称 */
export function expandSpecsToTerminateCols(specs: SpecSchedule[]): TerminateMatrixCol[] {
  const cols: TerminateMatrixCol[] = [];
  for (const spec of specs) {
    const st = (spec.status || '').toLowerCase();
    const specTerminal = st === 'completed' || st === 'terminated';
    const skus = spec.linkedSkus ?? [];
    if (skus.length === 0) {
      cols.push({
        colKey: spec.id,
        specId: spec.id,
        ipdSkuId: 0,
        title: formatSpecRowTitle(spec),
        specTerminal,
        specSkuCount: 1,
        isFirstColOfSpec: true,
      });
      continue;
    }
    skus.forEach((sku, index) => {
      cols.push({
        colKey: `${spec.id}:${sku.ipdSkuId}`,
        specId: spec.id,
        ipdSkuId: sku.ipdSkuId,
        title: skuSpecColumnTitle(sku),
        skuCode: (sku.skuCode || '').trim(),
        specTerminal,
        specSkuCount: skus.length,
        isFirstColOfSpec: index === 0,
      });
    });
  }
  return cols;
}

export function buildTerminateMatrixCells(
  specs: SpecSchedule[],
  cols: TerminateMatrixCol[],
  milestones: Milestone[],
  manDaysByMilestoneId?: Map<string, number>,
  autoCompletionIds?: Set<string>,
): Record<string, TerminateMatrixCell> {
  const specById = new Map(specs.map(s => [s.id, s]));
  const out: Record<string, TerminateMatrixCell> = {};
  for (const m of milestones) {
    const fromNode = manDaysByMilestoneId?.get(String(m.id));
    const baseline = typeof fromNode === 'number' && fromNode >= 0 ? fromNode : 0;
    const autoCompletion = !!autoCompletionIds?.has(String(m.id));
    for (const col of cols) {
      const spec = specById.get(col.specId);
      const statusBefore = spec
        ? effectiveSkuNodeStatus(spec, String(m.id), col.ipdSkuId)
        : 'pending';
      out[terminateCellKey(col.colKey, m.id)] = { statusBefore, baselineManDays: baseline, autoCompletion };
    }
  }
  return out;
}

export function isTerminateSkuColSelected(
  col: TerminateMatrixCol,
  selectedKeys: Set<string>,
): boolean {
  if (col.ipdSkuId <= 0) return true;
  return selectedKeys.has(col.colKey);
}

export function hasTerminableSelection(
  cols: TerminateMatrixCol[],
  selectedKeys: Set<string>,
): boolean {
  const groups = new Map<string, TerminateMatrixCol[]>();
  for (const col of cols) {
    if (col.specTerminal) continue;
    const list = groups.get(col.specId) ?? [];
    list.push(col);
    groups.set(col.specId, list);
  }
  for (const group of groups.values()) {
    const skuCols = group.filter(c => c.ipdSkuId > 0);
    if (skuCols.length === 0) return true;
    if (skuCols.some(c => selectedKeys.has(c.colKey))) return true;
  }
  return false;
}

export function sumTerminateConsumedManDays(
  cols: TerminateMatrixCol[],
  milestones: Milestone[],
  cells: Record<string, TerminateMatrixCell>,
  choices: Record<string, SpecTerminateNodeChoice>,
  selectedKeys?: Set<string>,
): { consumed: number; detail: string } {
  const selected = selectedKeys ?? new Set(cols.map(x => x.colKey));
  const included = cols.filter(
    c => !c.specTerminal && isTerminateSkuColSelected(c, selected),
  );
  const firstSelectedOfSpec = new Set<string>();
  for (const col of included) {
    if (!firstSelectedOfSpec.has(col.specId)) firstSelectedOfSpec.add(col.colKey);
  }
  const perCol: { title: string; days: number }[] = [];
  let total = 0;
  for (const col of included) {
    let colSum = 0;
    for (const m of milestones) {
      const key = terminateCellKey(col.colKey, m.id);
      const before = cells[key]?.statusBefore ?? 'pending';
      const baseline = cells[key]?.baselineManDays ?? 0;
      const status = isTerminateCellLocked(before, col.specTerminal)
        ? (before === 'completed' ? 'completed' : before)
        : (choices[key] ?? defaultTerminateChoice(before));
      if (status !== 'completed' && status !== 'terminated') continue;
      if (m.mergeAcrossSpecs) {
        if (firstSelectedOfSpec.has(col.colKey)) colSum += baseline;
      } else {
        colSum += baseline;
      }
    }
    perCol.push({ title: col.title, days: colSum });
    total += colSum;
  }
  if (perCol.every(x => x.days === 0)) {
    return { consumed: 0, detail: '无已完成/已终止节点工时；进行中与待开始不计入' };
  }
  const formula = perCol.filter(x => x.days > 0).map(x => `${x.title}(${x.days})`).join(' + ');
  return {
    consumed: total,
    detail: `各列合计（跨规格合并节点按规格计一次，不按 SKU 倍增）：${formula}；进行中与待开始不计入`,
  };
}
